import React, { useCallback, useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import uuid from "react-uuid";
import { toast } from "react-toastify";
import Loading from "../../../common/components/loading/loading";
import {
  addTradeNegotiationMetadataField,
  deleteTradeNegotiationMetadataField,
  getTradeNegotiationMetadataFieldsByMarket,
  updateTradeNegotiationMetadataField,
} from "../../../common/services/marketdata/tradeNegotiationMetadataService";
import { useTranslation } from "react-i18next";
import TradingMarketNegotiationMetadataInput from "./tradingMarketNegotiationMetadataInput";
import TradingMarketNegotiationMetadataTable from "./tradingMarketNegotiationMetadataTable";

const TradingMarketNegotiationMetadata = ({ allowEdit, marketId }) => {
  const { t } = useTranslation(["trading"]);
  const [fields, setFields] = useState([]);
  const [selectedField, setSelectedField] = useState(null);
  const [fieldToDelete, setFieldToDelete] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isNew, setIsNew] = useState(true);

  const sortFields = useCallback((fieldsToSort) => {
    return [...fieldsToSort].sort((left, right) => {
      const groupCompare = (left.fieldGroupingName || "").localeCompare(
        right.fieldGroupingName || ""
      );
      if (groupCompare !== 0) return groupCompare;
      if ((left.sortOrder || 0) !== (right.sortOrder || 0)) {
        return (left.sortOrder || 0) - (right.sortOrder || 0);
      }
      return (left.fieldName || "").localeCompare(right.fieldName || "");
    });
  }, []);

  const loadFields = useCallback(async () => {
    setIsLoading(true);
    try {
      const loadedFields = await getTradeNegotiationMetadataFieldsByMarket(marketId);
      setFields(sortFields(loadedFields || []));
    } catch (error) {
      console.error("Error loading trade negotiation metadata:", error);
      setFields([]);
      toast.error(t("TradeNegotiationMetadataLoadFailed"));
    } finally {
      setIsLoading(false);
    }
  }, [marketId, sortFields, t]);

  useEffect(() => {
    loadFields();
  }, [loadFields]);

  const handleAdd = useCallback(() => {
    setSelectedField(null);
    setIsNew(true);
    setShowEditModal(true);
  }, []);

  const handleEdit = useCallback((field) => {
    setSelectedField(field);
    setIsNew(false);
    setShowEditModal(true);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setShowEditModal(false);
    setSelectedField(null);
  }, []);

  const handleSave = useCallback(
    async (payload) => {
      try {
        const savedField = isNew
          ? await addTradeNegotiationMetadataField(payload)
          : await updateTradeNegotiationMetadataField(payload);

        setFields((currentFields) => {
          const updatedFields = isNew
            ? [...currentFields, savedField]
            : currentFields.map((field) =>
                field.fieldDefinitionId === savedField.fieldDefinitionId
                  ? savedField
                  : field
              );
          return sortFields(updatedFields);
        });

        toast.success(t("TradeNegotiationMetadataSaved"));
        handleCloseEditModal();
      } catch (error) {
        console.error("Error saving trade negotiation metadata:", error);
        toast.error(error.response?.data || t("TradeNegotiationMetadataSaveFailed"));
        throw error;
      }
    },
    [handleCloseEditModal, isNew, sortFields, t]
  );

  const handleDeleteRequested = useCallback((field) => {
    setFieldToDelete(field);
    setShowDeleteModal(true);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
    setFieldToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!fieldToDelete) return;

    try {
      await deleteTradeNegotiationMetadataField(fieldToDelete.fieldDefinitionId, uuid());
      setFields((currentFields) =>
        currentFields.filter(
          (field) => field.fieldDefinitionId !== fieldToDelete.fieldDefinitionId
        )
      );
      toast.success(t("TradeNegotiationMetadataDeleted"));
      handleCloseDeleteModal();
    } catch (error) {
      console.error("Error deleting trade negotiation metadata:", error);
      toast.error(t("TradeNegotiationMetadataDeleteFailed"));
    }
  }, [fieldToDelete, handleCloseDeleteModal, t]);

  if (isLoading) return <Loading />;

  return (
    <div>
      <div className="row mb-3">
        <div className="col-md-3 flex-column d-flex">
          {allowEdit && (
            <button
              type="button"
              onClick={handleAdd}
              className="btn btn-sm btn-primary float-sm-right"
              style={{ marginTop: "auto" }}
            >
              {t("AddNegotiationField")}
            </button>
          )}
        </div>
        <div className="col-md-9 d-flex justify-content-end align-items-center">
          <small className="form-text text-muted m-0">
            {t("TradeNegotiationMetadataHelpText")}
          </small>
        </div>
      </div>

      <Modal
        show={showEditModal}
        onHide={handleCloseEditModal}
        backdrop="static"
        keyboard={false}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {isNew
              ? t("CreateTradeNegotiationMetadataField")
              : t("EditTradeNegotiationMetadataField")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <TradingMarketNegotiationMetadataInput
            allowEdit={allowEdit}
            field={selectedField}
            isNew={isNew}
            marketId={marketId}
            onClose={handleCloseEditModal}
            onSave={handleSave}
          />
        </Modal.Body>
      </Modal>

      <Modal show={showDeleteModal} onHide={handleCloseDeleteModal}>
        <Modal.Header closeButton>
          <Modal.Title>{t("DeleteTradeNegotiationMetadataField")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {t("ConfirmDeleteTradeNegotiationMetadataField")} "
            <b>{fieldToDelete?.fieldName}</b>"?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteModal}>
            {t("Cancel")}
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            {t("Delete")}
          </Button>
        </Modal.Footer>
      </Modal>

      <TradingMarketNegotiationMetadataTable
        allowEdit={allowEdit}
        fields={fields}
        onDelete={handleDeleteRequested}
        onEdit={handleEdit}
      />
    </div>
  );
};

export default TradingMarketNegotiationMetadata;